import { useMemo, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Check, CircleAlert, Clock3, Info, Target, Timer, X } from 'lucide-react';
import type { DiagramJson, MediaItem, TiptapNode } from '@/shared/components/PromptRenderer';
import { PromptRenderer } from '@/shared/components/PromptRenderer';
import {
  Badge,
  Button,
  PageContainer,
  ProgressRing,
  Surface,
  type BadgeVariant,
} from '@/interfaces/learner/ui';
import { useSessionSummary } from '@/shared/api/hooks';
import { formatDurationBetween, formatDurationPrecise } from '@/shared/utils/formatDuration';
import type { StudySessionStep } from '@formency/shared';
import type { AnsweredStep } from '@/features/session-player/types';
import { stepTypeLabel, formatAnswer } from '@/features/session-player/answerFormatting';
import { getGradingOutcome, type GradingOutcome } from '@/features/session-player/sessionUtils';
import { reconstructAnsweredSteps } from './revisionSessionReview';

const EASE = [0.16, 1, 0.3, 1] as const;

interface OutcomeTheme {
  badge: BadgeVariant;
  label: string;
  icon: ReactNode;
  panelBackground: string;
  panelBorder: string;
  panelText: string;
}

const OUTCOME_THEMES: Record<GradingOutcome | 'info' | 'pending', OutcomeTheme> = {
  correct: {
    badge: 'success',
    label: 'Correct',
    icon: <Check size={13} strokeWidth={2.4} />,
    panelBackground: 'var(--color-success-subtle)',
    panelBorder: 'var(--color-success-outline)',
    panelText: 'var(--color-success-text)',
  },
  partial: {
    badge: 'warning',
    label: 'Partially correct',
    icon: <CircleAlert size={13} strokeWidth={2.2} />,
    panelBackground: 'var(--color-warning-subtle)',
    panelBorder: 'var(--color-warning-outline)',
    panelText: 'var(--color-warning-text)',
  },
  incorrect: {
    badge: 'error',
    label: 'Incorrect',
    icon: <X size={13} strokeWidth={2.4} />,
    panelBackground: 'var(--color-error-subtle)',
    panelBorder: 'var(--color-error)',
    panelText: 'var(--color-error-text)',
  },
  info: {
    badge: 'info',
    label: 'Info',
    icon: <Info size={13} strokeWidth={2.2} />,
    panelBackground: 'var(--color-info-subtle)',
    panelBorder: 'var(--color-info-outline)',
    panelText: 'var(--color-info-text)',
  },
  pending: {
    badge: 'surface',
    label: 'Not answered',
    icon: <Clock3 size={13} strokeWidth={2.2} />,
    panelBackground: 'var(--color-surface-muted)',
    panelBorder: 'var(--color-surface-border)',
    panelText: 'var(--color-text-secondary)',
  },
};

export default function RevisionHistoryPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { data: session, isLoading, isError, refetch } = useSessionSummary(sessionId);

  const sortedSteps = useMemo(() => {
    if (!session?.studySessionSteps) return [];
    return [...session.studySessionSteps].sort((a, b) => a.position - b.position);
  }, [session]);

  const answeredSteps = useMemo(() => reconstructAnsweredSteps(sortedSteps), [sortedSteps]);

  const promptDocs = useMemo(
    () => sortedSteps.map((step) => step.stepSnapshotPublicJson.prompt as TiptapNode),
    [sortedSteps]
  );

  const questionSteps = useMemo(
    () => sortedSteps.filter((step) => step.stepKind !== 'info'),
    [sortedSteps]
  );

  const outcomeCounts = useMemo(() => {
    let correct = 0;
    let partial = 0;
    let incorrect = 0;
    let unanswered = 0;

    sortedSteps.forEach((step, index) => {
      if (step.stepKind === 'info') return;
      const answered = answeredSteps.get(index);
      if (!answered) {
        unanswered += 1;
        return;
      }

      const outcome = getGradingOutcome(answered.result);
      if (outcome === 'correct') correct += 1;
      else if (outcome === 'partial') partial += 1;
      else incorrect += 1;
    });
    return { correct, partial, incorrect, unanswered };
  }, [sortedSteps, answeredSteps]);

  if (isLoading) {
    return (
      <PageContainer
        width="wide"
        className="pb-20"
        style={{ paddingTop: 'var(--page-top-spacing)' }}
      >
        <RevisionDetailSkeleton />
      </PageContainer>
    );
  }

  if (isError || !session) {
    return (
      <PageContainer
        width="wide"
        className="pb-20"
        style={{ paddingTop: 'var(--page-top-spacing)' }}
      >
        <Surface tone="solid" padding={28}>
          <p className="text-eyebrow" style={{ color: 'var(--color-accent-text)' }}>
            Session unavailable
          </p>
          <h1 className="text-section-title mt-3">This revision session could not be loaded.</h1>
          <p className="text-body mt-4" style={{ maxWidth: 'var(--measure-panel-copy)' }}>
            The session may have been removed or the summary endpoint may be temporarily
            unavailable.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button variant="accent" onPress={() => void refetch()}>
              Try again
            </Button>
            <Button variant="secondary" onPress={() => navigate('/revision')}>
              Back to history
            </Button>
          </div>
        </Surface>
      </PageContainer>
    );
  }

  const isAbandoned = session.status === 'abandoned';
  const scorePct = Math.round(session.scorePct ?? 0);
  const questionsCorrect = session.questionsCorrect ?? 0;
  const questionsTotal = session.questionsTotal ?? questionSteps.length;
  const startedAt = new Date(session.startedAt);
  const formattedDate = startedAt.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const formattedTime = startedAt.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const duration = formatDurationBetween(session.startedAt, session.completedAt ?? null);
  const title = session.title?.trim() || 'Revision session';
  const summaryValue = isAbandoned ? Math.round(session.completionPct ?? 0) : scorePct;
  const summaryLabel = isAbandoned ? 'Completed' : 'Accuracy';
  const summaryDescription = isAbandoned
    ? `${answeredSteps.size} step${answeredSteps.size === 1 ? '' : 's'} reached before the session ended`
    : `${questionsCorrect} of ${questionsTotal} questions answered correctly`;

  return (
    <PageContainer width="wide" className="relative pb-24">
      <section style={{ paddingTop: 'var(--page-top-spacing)' }}>
        <div className="grid gap-8 learner-double:grid-cols-[minmax(0,1fr)_380px]">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <Link
              to="/revision"
              className="text-meta-muted inline-flex items-center gap-1.5 transition-colors"
            >
              <ArrowLeft size={15} strokeWidth={2.2} />
              Back to revision history
            </Link>

            <p className="text-eyebrow mt-6" style={{ color: 'var(--color-accent)' }}>
              Revision review
            </p>
            <h1 className="text-hero-title mt-2" style={{ maxWidth: 'var(--measure-hero)' }}>
              {title}
            </h1>
            <p className="text-body mt-4" style={{ maxWidth: 'var(--measure-copy)' }}>
              {formattedDate} at {formattedTime} · {questionsTotal} question
              {questionsTotal === 1 ? '' : 's'} · {duration}
            </p>
            <p className="text-body-muted mt-3" style={{ maxWidth: 'var(--measure-copy)' }}>
              {isAbandoned
                ? 'This session ended before every step was answered. Review what was completed and revisit the rest when you are ready.'
                : 'Review each step calmly, see where your answers held up, and use the explanation blocks to decide what needs another pass.'}
            </p>

            <div className="mt-5 flex flex-wrap gap-2.5">
              <Badge variant={isAbandoned ? 'surface' : 'accent'}>
                {isAbandoned ? 'Abandoned' : 'Completed'}
              </Badge>
              <Badge variant="surface">
                {questionsCorrect}/{questionsTotal} correct
              </Badge>
              <Badge variant="muted">{sortedSteps.length} steps reviewed</Badge>
            </div>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.08, ease: EASE }}
            className="learner-double:pt-6"
          >
            <div className="learner-double:sticky learner-double:top-[var(--sticky-top-offset)]">
              <Surface
                tone="solid"
                padding={24}
                style={{
                  background: isAbandoned
                    ? 'var(--color-surface-solid)'
                    : 'var(--color-accent-wash-strong)',
                }}
              >
                <p
                  className="text-eyebrow"
                  style={{
                    color: isAbandoned ? 'var(--color-text-muted)' : 'var(--color-accent-text)',
                  }}
                >
                  Session summary
                </p>

                <div className="mt-4 flex items-center gap-4">
                  <ProgressRing
                    value={summaryValue}
                    color={isAbandoned ? 'var(--color-warning)' : 'var(--color-accent)'}
                  />
                  <div className="min-w-0">
                    <p className="text-panel-title">
                      {summaryValue}% {summaryLabel.toLowerCase()}
                    </p>
                    <p className="text-meta mt-2">{summaryDescription}</p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <SummaryChip label="Duration" value={duration} tone="neutral" />
                  <SummaryChip label="Correct" value={`${outcomeCounts.correct}`} tone="success" />
                  <SummaryChip label="Partial" value={`${outcomeCounts.partial}`} tone="warning" />
                  <SummaryChip
                    label={outcomeCounts.unanswered > 0 ? 'Unanswered' : 'Incorrect'}
                    value={`${outcomeCounts.unanswered > 0 ? outcomeCounts.unanswered : outcomeCounts.incorrect}`}
                    tone={outcomeCounts.unanswered > 0 ? 'neutral' : 'error'}
                  />
                </div>
              </Surface>
            </div>
          </motion.aside>
        </div>

        <motion.div
          className="mt-9 grid gap-4 learner-single:grid-cols-2 learner-double:grid-cols-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.12, ease: EASE }}
        >
          <DetailMetricCard
            icon={<Target size={18} strokeWidth={2.1} />}
            label="Questions"
            value={`${questionsCorrect}/${questionsTotal}`}
            description="Correct answers across the scored steps."
          />
          <DetailMetricCard
            icon={<Clock3 size={18} strokeWidth={2.1} />}
            label="Answered steps"
            value={`${answeredSteps.size}/${sortedSteps.length}`}
            description="How far the session progressed before it ended."
          />
          <DetailMetricCard
            icon={<Check size={18} strokeWidth={2.1} />}
            label="Strong recall"
            value={`${outcomeCounts.correct}`}
            valueColor="var(--color-success-text)"
            description="Steps answered fully correctly."
          />
          <DetailMetricCard
            icon={<Timer size={18} strokeWidth={2.1} />}
            label="Session length"
            value={duration}
            description="From the moment the session started to its end."
          />
        </motion.div>

        <motion.section
          className="mt-10"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.18, ease: EASE }}
        >
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
                Step-by-step review
              </p>
              <h2 className="text-section-title mt-2">See exactly how this session unfolded.</h2>
            </div>
            <Badge variant="surface">{sortedSteps.length} steps</Badge>
          </div>

          <div className="mt-6 space-y-4">
            {sortedSteps.map((step, index) => {
              const answered = answeredSteps.get(index);
              const promptDoc = promptDocs[index];

              return (
                <RevisionStepCard
                  key={step.id}
                  step={step}
                  index={index}
                  answered={answered}
                  promptDoc={promptDoc}
                  isAbandoned={isAbandoned}
                />
              );
            })}
          </div>
        </motion.section>
      </section>
    </PageContainer>
  );
}

function DetailMetricCard({
  icon,
  label,
  value,
  description,
  valueColor,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  description: string;
  valueColor?: string;
}) {
  return (
    <Surface tone="solid" padding={20}>
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{
          background: 'var(--color-surface-muted)',
          color: 'var(--color-accent-text)',
          border: '1px solid var(--color-surface-border)',
        }}
      >
        {icon}
      </div>
      <p className="text-eyebrow mt-4" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </p>
      <p className="text-panel-title mt-2" style={{ color: valueColor }}>
        {value}
      </p>
      <p className="text-meta mt-2">{description}</p>
    </Surface>
  );
}

function SummaryChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'neutral' | 'success' | 'warning' | 'error';
}) {
  const styleByTone = {
    neutral: {
      background: 'var(--color-surface-solid)',
      border: '1px solid var(--color-surface-border)',
      color: 'var(--color-text)',
    },
    success: {
      background: 'var(--color-success-subtle)',
      border: '1px solid var(--color-success-outline)',
      color: 'var(--color-success-text)',
    },
    warning: {
      background: 'var(--color-warning-subtle)',
      border: '1px solid var(--color-warning-outline)',
      color: 'var(--color-warning-text)',
    },
    error: {
      background: 'var(--color-error-subtle)',
      border: '1px solid var(--color-error)',
      color: 'var(--color-error-text)',
    },
  } as const;

  const toneStyles = styleByTone[tone];

  return (
    <div
      className="rounded-[var(--control-radius-md)] px-3.5 py-3"
      style={{
        background: toneStyles.background,
        border: toneStyles.border,
      }}
    >
      <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </p>
      <p className="text-list-title mt-2" style={{ color: toneStyles.color }}>
        {value}
      </p>
    </div>
  );
}

function RevisionStepCard({
  step,
  index,
  answered,
  promptDoc,
  isAbandoned,
}: {
  step: StudySessionStep;
  index: number;
  answered?: AnsweredStep & { responseTimeMs: number | null };
  promptDoc: TiptapNode;
  isAbandoned: boolean;
}) {
  const isInfo = step.stepKind === 'info';
  const outcomeKey: GradingOutcome | 'info' | 'pending' = isInfo
    ? 'info'
    : answered
      ? getGradingOutcome(answered.result)
      : 'pending';
  const theme = OUTCOME_THEMES[outcomeKey];
  const answerSummary = answered ? formatAnswer(step, answered) : null;
  const scoreLabel =
    !isInfo && answered ? `${answered.result.score}/${answered.result.maxScore}` : null;
  const media =
    Array.isArray(step.mediaRefsJson) && step.mediaRefsJson.length > 0
      ? (step.mediaRefsJson as MediaItem[])
      : null;
  const diagram =
    step.diagramJson && typeof step.diagramJson === 'object'
      ? (step.diagramJson as DiagramJson)
      : null;
  const statusCopy = isInfo
    ? 'Information step'
    : answered?.responseTimeMs != null
      ? `Answered in ${formatDurationPrecise(answered.responseTimeMs)}`
      : isAbandoned
        ? 'Not reached before the session ended'
        : 'No answer recorded';

  return (
    <Surface tone="solid" className="overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.3), ease: EASE }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="surface">Step {index + 1}</Badge>
              <Badge variant="surface">{stepTypeLabel(isInfo ? 'info' : step.questionType)}</Badge>
              <Badge variant={theme.badge}>
                <span className="inline-flex items-center gap-1.5">
                  {theme.icon}
                  {theme.label}
                </span>
              </Badge>
            </div>

            <p className="text-meta mt-3">{statusCopy}</p>
          </div>

          {scoreLabel ? (
            <div className="shrink-0 text-right">
              <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
                Score
              </p>
              <p className="text-card-title mt-2" style={{ color: theme.panelText }}>
                {scoreLabel}
              </p>
            </div>
          ) : null}
        </div>

        <div style={{ borderTop: '1px solid var(--color-surface-border)' }}>
          <div className="px-5 py-5">
            <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
              Prompt
            </p>
            <div className="text-body mt-4" style={{ color: 'var(--color-text-secondary)' }}>
              <PromptRenderer
                doc={promptDoc}
                media={media}
                diagramJson={diagram}
                className="f-prompt-prose"
              />
            </div>
          </div>

          {!isInfo ? (
            <div
              className="grid gap-4 px-5 py-5 learner-double:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]"
              style={{ borderTop: '1px solid var(--color-surface-border)' }}
            >
              <div className="min-w-0">
                <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
                  Your answer
                </p>
                <div
                  className="mt-4 rounded-[var(--control-radius-lg)] px-4 py-3"
                  style={{
                    background: 'var(--color-surface-muted)',
                    border: '1px solid var(--color-surface-border)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {answerSummary ? (
                    <div className="text-body">{answerSummary}</div>
                  ) : (
                    <p className="text-body-muted">No answer was recorded for this step.</p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {answered?.result.explanation ? (
                  <FeedbackPanel
                    label="Explanation"
                    text={answered.result.explanation}
                    background={theme.panelBackground}
                    border={theme.panelBorder}
                    color={theme.panelText}
                  />
                ) : null}

                {answered?.result.feedback ? (
                  <FeedbackPanel
                    label="Feedback"
                    text={answered.result.feedback}
                    background={theme.panelBackground}
                    border={theme.panelBorder}
                    color={theme.panelText}
                  />
                ) : null}

                {!answered?.result.explanation && !answered?.result.feedback ? (
                  <FeedbackPanel
                    label="Review note"
                    text={
                      answered
                        ? 'No additional explanation was attached to this step.'
                        : 'This step was not answered, so there is no marked explanation yet.'
                    }
                    background="var(--color-surface-muted)"
                    border="var(--color-surface-border)"
                    color="var(--color-text-secondary)"
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </motion.div>
    </Surface>
  );
}

function FeedbackPanel({
  label,
  text,
  background,
  border,
  color,
}: {
  label: string;
  text: string;
  background: string;
  border: string;
  color: string;
}) {
  return (
    <div
      className="rounded-[var(--control-radius-lg)] px-4 py-3"
      style={{
        background,
        border: `1px solid ${border}`,
      }}
    >
      <p className="text-eyebrow" style={{ color, opacity: 0.92 }}>
        {label}
      </p>
      <p className="text-meta mt-2" style={{ color }}>
        {text}
      </p>
    </div>
  );
}

function RevisionDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Surface tone="solid" padding={28}>
        <div
          className="h-4 w-32 rounded-full"
          style={{ background: 'var(--color-surface-muted)' }}
        />
        <div
          className="mt-5 h-14 w-2/3 rounded-[20px]"
          style={{ background: 'var(--color-surface-muted)' }}
        />
        <div
          className="mt-4 h-5 w-1/2 rounded-full"
          style={{ background: 'var(--color-surface-muted)' }}
        />
      </Surface>

      <div className="grid gap-4 learner-single:grid-cols-2 learner-double:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <Surface key={item} tone="solid" padding={20}>
            <div
              className="h-10 w-10 rounded-full"
              style={{ background: 'var(--color-surface-muted)' }}
            />
            <div
              className="mt-4 h-3 w-20 rounded-full"
              style={{ background: 'var(--color-surface-muted)' }}
            />
            <div
              className="mt-3 h-8 w-28 rounded-full"
              style={{ background: 'var(--color-surface-muted)' }}
            />
          </Surface>
        ))}
      </div>
    </div>
  );
}
